import { Vulnerabilities as VulnerabilitiesIcon } from '@/components/icons';
import { NoDataProps } from '@/components/ui/NoData';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/ui/utils/createNoDataConfig';

interface VulnerabilityNoDataConfigParams {
  isAllEmpty: boolean;
  isFilteredEmpty: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Callback when "Get Started" button is clicked. Only provide when NOT in drawer context. */
  onGetStarted?: () => void;
  /** Whether the component is rendered in drawer context (affects empty state messaging) */
  inDrawer?: boolean;
}

/**
 * Generates NoData configuration for vulnerability table empty states
 *
 * Handles three scenarios:
 * 1. All Empty (Main Page) - First-time user with no vulnerabilities discovered yet
 * 2. All Empty (Drawer) - Asset has no vulnerabilities (no Get Started button)
 * 3. Filtered Empty - User has vulnerabilities but current filters return none
 *
 * @param params - Configuration parameters
 * @returns NoData component props
 */
export const getVulnerabilityNoDataConfig = ({
  isAllEmpty,
  isFilteredEmpty,
  hasActiveFilters,
  onClearFilters,
  onGetStarted,
  inDrawer = false,
}: VulnerabilityNoDataConfigParams): NoDataProps => {
  const icon = <VulnerabilitiesIcon className={NO_DATA_ICON_CLASS} />;

  // All Empty State
  if (isAllEmpty) {
    // Drawer context: simplified message, no Get Started button
    if (inDrawer) {
      return createAllEmptyConfig({
        entityName: 'No vulnerabilities found for this asset',
        icon,
      });
    }

    // Main page context: onboarding message with optional Get Started
    return createAllEmptyConfig({
      entityName: 'No Vulnerabilities to Show Yet',
      icon,
      onGetStarted,
    });
  }

  // Filtered Empty State
  return createFilteredEmptyConfig({
    entityName: 'Vulnerabilities',
    icon,
    hasActiveFilters: hasActiveFilters && isFilteredEmpty,
    onClearFilters,
  });
};
